// Clients (users/{uid}/clients/{id}) and the figures worked out from their
// invoices. A port of client.dart and client_stats.dart.

import { compareStamps, daysBetween, type IsoDate } from '@/core/dates';
import { type ClientSnapshot, initialsOf, type Invoice, isDraft, statusOn, totalsOf } from '@/core/invoice';
import { type Currency, currencyOf, Money } from '@/core/money';

export interface Client {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  /** Private to the user; never printed on invoices. */
  notes: string;
  /** The business it's a client of; null for clients from before there could be several (the first business's). */
  businessId: string | null;
  createdAt: string;
  updatedAt: string;
}

type Json = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function clientFromJson(json: Json): Client {
  const id = str(json.id);
  if (!id) throw new Error('Not a client');
  return {
    id,
    name: str(json.name),
    company: str(json.company),
    email: str(json.email),
    phone: str(json.phone),
    address: str(json.address),
    notes: str(json.notes),
    businessId: typeof json.businessId === 'string' && json.businessId ? json.businessId : null,
    createdAt: str(json.createdAt),
    updatedAt: str(json.updatedAt),
  };
}

/** Field for field as Client.toJson in the phone app: businessId only when set. */
export function clientToJson(c: Client): Json {
  const { businessId, ...rest } = c;
  return businessId ? { ...rest, businessId } : rest;
}

/** The company when set, otherwise the person's name. */
export const displayName = (c: Pick<Client, 'name' | 'company'>) => (c.company.trim() ? c.company.trim() : c.name.trim());

export const clientInitials = (c: Client) => initialsOf(displayName(c));

/** The copy stored on each invoice. */
export function toSnapshot(c: Client): ClientSnapshot {
  return { name: displayName(c), email: c.email, phone: c.phone, address: c.address };
}

/** The email, phone or first address line, whichever they have first. */
export function contactLine(c: Client): string {
  return [c.email, c.phone, c.address.split('\n')[0]].map((s) => s.trim()).find((s) => s.length > 0) ?? '';
}

/** By display name, ignoring case. */
export function sortClients(clients: Client[]): Client[] {
  return [...clients].sort((a, b) => displayName(a).toLowerCase().localeCompare(displayName(b).toLowerCase()));
}

/** Somewhat like an email: something@domain.tld, no spaces. */
export const isEmailAddress = (text: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(text.trim());

export interface ClientStats {
  currency: Currency;
  invoiceCount: number;
  /** Still owed on sent invoices, overdue ones included. */
  owed: Money;
  /** The part of [owed] that is past due. */
  overdue: Money;
  /** Everything received, ever. */
  paid: Money;
  /** Average days from sending to the last payment, over paid invoices. */
  averageDaysToPay: number | null;
}

/**
 * What a client owes and has paid, in the currency of their latest invoice;
 * invoices in other currencies are left out rather than mixed in.
 */
export function clientStats(clientId: string, all: Invoice[], today: IsoDate): ClientStats {
  const invoices = all.filter((i) => i.clientId === clientId).sort((a, b) => compareStamps(b.issueDate, a.issueDate));
  const currency = currencyOf(invoices[0]?.currency ?? 'NGN');
  let owed = Money.zero(currency);
  let overdue = Money.zero(currency);
  let paid = Money.zero(currency);
  const daysToPay: number[] = [];
  for (const invoice of invoices.filter((i) => i.currency === currency.code)) {
    const t = totalsOf(invoice);
    paid = paid.plus(t.paid);
    if (isDraft(invoice)) continue;
    owed = owed.plus(t.balance);
    const status = statusOn(invoice, today);
    if (status === 'overdue') overdue = overdue.plus(t.balance);
    if (status === 'paid' && invoice.payments.length > 0) {
      const last = invoice.payments.map((p) => p.date).reduce((a, b) => (a > b ? a : b));
      daysToPay.push(Math.min(Math.max(daysBetween(invoice.sentAt!, last), 0), 9999));
    }
  }
  return {
    currency,
    invoiceCount: invoices.length,
    owed,
    overdue,
    paid,
    averageDaysToPay: daysToPay.length ? Math.round(daysToPay.reduce((a, b) => a + b, 0) / daysToPay.length) : null,
  };
}

/** Pays within two weeks on average with nothing overdue. */
export const isGoodPayer = (s: ClientStats) => s.overdue.isZero && s.averageDaysToPay !== null && s.averageDaysToPay <= 14;
